import {generatePath, matchPath} from 'react-router-dom'

import type {paths} from '../../../../../open-api/fancy/generated-client'

export function urlToPathGroupingHash(urlString: string): string {
  return (
    Object.entries(pathParamsToGroupBy)
      .map(([p, toIgnore]) => {
        const url = new URL(urlString)
        const reactRouterPath = p.replace(/\{([^}]+)\}/g, (_, m) => `:${m.replace('-', '')}`)
        const match = matchPath(reactRouterPath, url.pathname)
        if (match !== null) {
          const reactRouterIgnored = toIgnore.map(param => param.replace(/-/g, ''))
          const params = Object.fromEntries(
            Object.entries(match.params).map(([key, value]) => [
              key,
              reactRouterIgnored.includes(key) ? value : `:${key}`
            ])
          )
          return generatePath(reactRouterPath, params)
        } else {
          return null
        }
      })
      .find(x => x) ?? urlString
  )
}

// Normally we would always group on path names but for some paths we should probably group separately
// This mapping will avoid grouping on the path params passed in the array.
//
// ex: for '/3/{ContentAreaP-ContentArea}/adaptive/answers'
//     we want a separate grouping for `/3/math/adaptive/answers` and`/3/ela/adaptive/answers`
//     so we should use`['ContentAreaP-ContentArea']`
const pathParamsToGroupBy: {
  [P in keyof Required<paths>]: string[]
} = {
  '/3/{ContentAreaP-ContentArea}/adaptive/answers': ['ContentAreaP-ContentArea'],
  '/3/{ContentAreaP-ContentArea}/adaptive/questions/arbitrary': ['ContentAreaP-ContentArea'],
  '/3/{ContentAreaP-ContentArea}/adaptive/rewards': ['ContentAreaP-ContentArea'],
  '/3/{ContentAreaP-ContentArea}/adaptive-progresses': ['ContentAreaP-ContentArea'],
  '/3/{ContentAreaP-ContentArea}/assessments/questions/arbitrary': ['ContentAreaP-ContentArea'],
  '/3/{ContentAreaP-ContentArea}/constructed-responses': ['ContentAreaP-ContentArea'],
  '/3/{ContentAreaP-ContentArea}/depth-of-knowledge/question-sets': ['ContentAreaP-ContentArea'],
  '/3/{ContentAreaP-ContentArea}/diagnostic/questions/arbitrary': ['ContentAreaP-ContentArea'],
  '/3/{ContentAreaP-ContentArea}/domains/exclusions': ['ContentAreaP-ContentArea'],
  '/3/{ContentAreaP-ContentArea}/ibls': ['ContentAreaP-ContentArea'],
  '/3/{ContentAreaP-ContentArea}/questions/arbitrary': ['ContentAreaP-ContentArea'],
  '/3/{ContentAreaP-ContentArea}/standard-sets': ['ContentAreaP-ContentArea'],
  '/3/{ContentAreaP-ContentArea}/skill-proficiencies': ['ContentAreaP-ContentArea'],
  '/3/{ContentAreaP-ContentArea}/skill-proficiencies/progress': ['ContentAreaP-ContentArea'],
  '/3/subjects/{SubjectP-Subject}/products/{ProductP-AbbreviatedProduct}/assignments/{SubjectProductAssignmentP-Int64}':
    ['SubjectP-Subject', 'ProductP-AbbreviatedProduct'],
  '/3/subjects/{SubjectP-Subject}/products/{ProductP-AbbreviatedProduct}/assignments/{SubjectProductAssignmentP-Int64}/sessions':
    ['SubjectP-Subject', 'ProductP-AbbreviatedProduct'],
  '/3/subjects/{SubjectP-Subject}/products/{ProductP-AbbreviatedProduct}/assignments/{SubjectProductAssignmentP-Int64}/passages':
    ['SubjectP-Subject', 'ProductP-AbbreviatedProduct'],
  '/3/subjects/{SubjectP-Subject}/products/{ProductP-AbbreviatedProduct}/sessions': [
    'SubjectP-Subject',
    'ProductP-AbbreviatedProduct'
  ],
  '/3/subjects/{SubjectP-Subject}/products/{ProductP-AbbreviatedProduct}/sessions/{SubjectProductSessionP-Int64}':
    ['SubjectP-Subject', 'ProductP-AbbreviatedProduct'],
  '/3/subjects/{SubjectP-Subject}/products/{ProductP-AbbreviatedProduct}/sessions/{SubjectProductSessionP-Int64}/attempts':
    ['SubjectP-Subject', 'ProductP-AbbreviatedProduct'],
  '/3/subjects/{SubjectP-Subject}/products/{ProductP-AbbreviatedProduct}/sessions/{SubjectProductSessionP-Int64}/answers':
    ['SubjectP-Subject', 'ProductP-AbbreviatedProduct'],
  '/3/subjects/{SubjectP-Subject}/products/{ProductP-AbbreviatedProduct}/sessions/{SubjectProductSessionP-Int64}/answers/retries':
    ['SubjectP-Subject', 'ProductP-AbbreviatedProduct'],
  '/3/subjects/{SubjectP-Subject}/products/{ProductP-AbbreviatedProduct}/sessions/{SubjectProductSessionP-Int64}/sentiments/{SubjectProductSessionSentimentP-Sentiment}':
    ['SubjectP-Subject', 'ProductP-AbbreviatedProduct'],
  '/3/courses/{V3CourseP-CourseId}/subjects/{CourseSubjectP-Subject}/products/{CourseProductP-AbbreviatedProduct}/sessions':
    ['SubjectP-Subject', 'ProductP-AbbreviatedProduct'],
  '/3/courses/{V3CourseP-CourseId}/subjects/{CourseSubjectP-Subject}/products/{CourseProductP-AbbreviatedProduct}/settings/{CourseSubjectProductSettingsR-StudentId}':
    ['SubjectP-Subject', 'ProductP-AbbreviatedProduct'],
  '/3/courses/{V3CourseP-CourseId}/subjects/{CourseSubjectP-Subject}/products/{CourseProductP-AbbreviatedProduct}/printable-assignments':
    ['SubjectP-Subject', 'ProductP-AbbreviatedProduct'],
  '/3/schools/{V3SchoolP-SchoolId}/subjects/{SchoolSubjectP-Subject}/products/{SchoolProductP-AbbreviatedProduct}/sessions':
    ['SubjectP-Subject', 'ProductP-AbbreviatedProduct'],

  '/health-check': [],
  '/routes.js': [],
  '/2/educator-academy/user': [],
  '/2/console/agent': [],
  '/2/console/impersonations': [],
  '/2/console/jobs': [],
  '/2/ping': [],
  '/2/teacher-ping': [],
  '/2/student-ping': [],
  '/2/boom': [],
  '/2/sloth': [],
  '/2/auth-check': [],
  '/2/admins/impersonations': [],
  '/2/premium-licenses': [],
  '/2/math-assessments/{MathAssessmentByIdR-MathAssessmentAssignmentId}': [],
  '/2/math-assessments/{MathAssessmentsAssessmentAnswersR-MathAssessmentAssignmentId}/assessment-answers':
    [],
  '/2/math-assessments/{MathAssessmentsAssessmentAnswerR-MathAssessmentAssignmentId}/assessment-answers/{MathAssessmentsAssessmentAnswerR-AssessmentAnswerId}':
    [],
  '/2/math-assessment-sessions/{MathAssessmentSessionsAssessmentAnswersR-MathAssessmentAssignmentSessionId}/assessment-answers':
    [],
  '/2/math-fact-practice-snapshots/latest': [],
  '/2/math-fact-practice-snapshots/latest3': [],
  '/2/math-fact-practice-usage': [],
  '/2/student-shares': [],
  '/2/students': [],
  '/2/students/me/ela-progress': [],
  '/2/students/me/ela-snapshots/latest': [],
  '/2/students/me/incomplete-assignment-count': [],
  '/2/students/me/login-events': [],
  '/2/students/me/math-adaptive-stats-student-domain-month/latest': [],
  '/2/students/me/time-spent': [],
  '/2/students/me': [],
  '/2/students/me/progress-and-growth': [],
  '/2/students/me/math-adaptive-pretest-status': [],
  '/2/students/sessions': [],
  '/2/students/duplicates': [],
  '/2/students/duplicates/merge': [],
  '/2/students/{ActivityFeedForStudentR-StudentId}/activity-feed': [],
  '/2/students/{StudentsArchiveForCourseR-StudentId}/archive/{StudentsArchiveForCourseR-CourseId}':
    [],
  '/2/students/{StudentsChangeCourseR-StudentId}/change-course': [],
  '/2/students/{StudentsElaSnapshotsR-StudentId}/ela-snapshots': [],
  '/2/students/{StudentsMathNumberBasicsSnapshotsLatestR-StudentId}/math/number-basics/snapshots/latest':
    [],
  '/2/students/{StudentsElaReportCardR-StudentId}/ela-report-card': [],
  '/2/students/{StudentStandardStatsR-StudentId}/standard-stats': [],
  '/2/students/{StudentsTeachersR-StudentId}/teachers': [],
  '/2/students/{StudentR-StudentId}': [],
  '/2/students/{StudentsWordStudySessionsR-StudentId}/word-study-sessions': [],
  '/2/students/{GoalsP-StudentId}/goals': [],
  '/2/students/{GoalsP-StudentId}/goals/{GoalP-StudentGoalId}': [],
  '/2/students/{StudentsCoTeachersR-NonEmptyStudentId}/co-teachers': [],
  '/2/students/{StudentPlacementsP-StudentId}/placements/math': [],
  '/2/session-summaries': [],
  '/2/student-idp-overlays': [],
  '/2/student-idp-overlays/{StudentsIdpOverlayR-IdpOverlayStudentId}': [],
  '/2/items': [],
  '/2/items/{ItemP-ItemId}': [],
  '/2/items/{ItemP-ItemId}/item-ownership': [],
  '/2/item-ownerships': [],
  '/2/courses': [],
  '/2/courses/current': [],
  '/2/courses/class-codes/{CoursesClassCodesR-ClassCode}': [],
  '/2/courses/{CourseR-CourseId}': [],
  '/2/courses/{CourseEnabledDashboardModesR-CourseId}/enabled-practice-modes': [],
  '/2/courses/{CourseSubjectEnabledDashboardModesR-CourseId}/subjects/{CourseSubjectEnabledDashboardModesR-Subject}/enabled-practice-modes':
    [],
  '/2/courses/{CourseStudentSubjectEnabledDashboardModesR-CourseId}/students/{CourseStudentSubjectEnabledDashboardModesR-StudentId}/subjects/{CourseStudentSubjectEnabledDashboardModesR-Subject}/enabled-practice-modes':
    [],
  '/2/courses/{ActivityFeedForCourseR-CourseId}/activity-feed': [],
  '/2/courses/{StudentHighlightsForCourseR-CourseId}/student-highlights': [],
  '/2/courses/{CoursesClassDomainGrowthR-CourseId}/class-grouping-report/domain-growth': [],
  '/2/courses/{ElaClassActivityDetailR-CourseId}/ela-class-activity/{ElaClassActivityDetailR-ElaArticleId}':
    [],
  '/2/courses/{ElaClassActivityR-CourseId}/ela-class-activity': [],
  '/2/courses/{ElaReadingLevelsR-CourseId}/ela-reading-levels': [],
  '/2/courses/{CoursesStudentGoalsR-CourseId}/student-goals': [],
  '/2/courses/{CoursesIdpStudentMergeR-CourseId}/idp-student-merge': [],
  '/2/courses/{CoursesMathAdaptiveStatsStudentDomainMonthLatestR-CourseId}/math-adaptive-stats-student-domain-month/latest':
    [],
  '/2/courses/{CoursesMathFactPracticeLevelsR-CourseId}/math-fact-practice-levels': [],
  '/2/courses/{CoursesMathReportCardSummariesR-CourseId}/math-report-card-summaries': [],
  '/2/courses/{CoursesMathDiagnosticsR-CourseId}/math-diagnostics': [],
  '/2/courses/{CoursesStudentsR-CourseId}/students': [],
  '/2/courses/{CoursesStudentStandardStatsR-CourseId}/student-standard-stats': [],
  '/2/courses/{CourseSubjectsR-CourseId}/subjects': [],
  '/2/courses/{CoursesTeacherR-CourseId}/teacher': [],
  '/2/courses/{CoursesWordStudyLevelsLatestPerStudentR-CourseId}/word-study-levels/latest-per-student':
    [],
  '/2/courses/{CourseReportsElaGroupsByLevelR-CourseId}/reports/ela/groups-by-level': [],
  '/2/courses/{CourseReportsElaPerformanceByStandardR-CourseId}/reports/ela/performance-by-standard':
    [],
  '/2/courses/{CoursesReportsMathP-CourseId}/reports/math/targeted/assignments/{CoursesReportsMathTargetedAssignmentP-MathStandardAssignmentId}/question-performance':
    [],
  '/2/courses/{CourseExpertsMathAdaptiveR-CourseId}/experts/math-adaptive': [],
  '/2/courses/{CourseExpertsElaAdaptiveSkillsR-CourseId}/experts/ela-adaptive-skills': [],
  '/2/courses/{CourseExpertsWordStudyR-CourseId}/experts/word-study': [],
  '/2/courses/unmanaged': [],
  '/2/course-memberships': [],
  '/2/course-memberships/{CourseMembershipR-CourseMembershipId}': [],
  '/2/districts': [],
  '/2/districts/{DistrictP-DistrictId}': [],
  '/2/districts/{DistrictP-DistrictId}/rosters': [],
  '/2/districts/{DistrictP-DistrictId}/rosters/sis-id-sync': [],
  '/2/districts/{DistrictP-DistrictId}/school-admins/{DistrictSchoolAdminP-SchoolAdminId}/school-memberships':
    [],
  '/2/districts/{DistrictP-DistrictId}/schools': [],
  '/2/districts/{DistrictP-DistrictId}/students/placements/{DistrictStudentsPlacementsContentAreaP-ContentArea}':
    [],
  '/2/districts/{DistrictP-DistrictId}/students/placements/{DistrictStudentsPlacementsContentAreaP-ContentArea}/fast-bridge':
    [],
  '/2/districts/{DistrictP-DistrictId}/students/placements/{DistrictStudentsPlacementsContentAreaP-ContentArea}/map':
    [],
  '/2/districts/{DistrictP-DistrictId}/students/placements/{DistrictStudentsPlacementsContentAreaP-ContentArea}/ngmt':
    [],
  '/2/districts/{DistrictP-DistrictId}/students/placements/{DistrictStudentsPlacementsContentAreaP-ContentArea}/reading':
    [],
  '/2/districts/{DistrictP-DistrictId}/identity-providers/associations': [],
  '/2/demo-districts/external': [],
  '/2/demo-districts': [],
  '/2/schools': [],
  '/2/schools/{SchoolsSchoolAdminSchoolMembershipsR-NonEmptySchoolId}/school-admins/{SchoolsSchoolAdminSchoolMembershipsR-SchoolAdminId}/school-memberships':
    [],
  '/2/schools/{SchoolP-SchoolId}': [],
  '/2/schools/{SchoolP-SchoolId}/assessments': [],
  '/2/schools/{SchoolP-SchoolId}/assessments2': [],
  '/2/schools/{SchoolP-SchoolId}/courses': [],
  '/2/schools/{SchoolP-SchoolId}/colleagues': [],
  '/2/schools/{SchoolP-SchoolId}/deletion-info': [],
  '/2/schools/{SchoolP-SchoolId}/math/assessments/assignments/{SchoolMathAssessmentsAssignmentP-MathAssessmentAssignmentId}/answers':
    [],
  '/2/schools/{SchoolP-SchoolId}/merge': [],
  '/2/schools/{SchoolP-SchoolId}/licenses': [],
  '/2/schools/{SchoolP-SchoolId}/identity-providers/associations': [],
  '/2/schools/{SchoolP-SchoolId}/identity-providers/sync': [],
  '/2/schools/search': [],
  '/2/schools/provision': [],
  '/2/licenses': [],
  '/2/licenses/{LicenseP-LicenseId}': [],
  '/2/license-allocations/{LicenseAllocationP-LicenseAllocationId}': [],
  '/2/ibl-sessions': [],
  '/2/teachers': [],
  '/2/teachers/me': [],
  '/2/teachers/accounts': [],
  '/2/teachers/affiliations': [],
  '/2/teachers/sessions': [],
  '/2/teachers/sessions/school-admin-impersonations': [],
  '/2/teachers/{TeacherR-TeacherId}': [],
  '/2/teachers/{TeachersAffiliationR-TeacherId}/affiliation': [],
  '/2/teachers/{TeacherLicenseAllocationsR-TeacherId}/license-allocations': [],
  '/2/teachers/{TeacherImpressionsR-TeacherId}/impressions': [],
  '/2/teachers/{TeacherReminderFinishOnboardingR-TeacherId}/reminders/finish-onboarding': [],
  '/2/teachers/{TeacherIdpCleverR-TeacherId}/idp/clever': [],
  '/2/teachers/{TeacherCoursesDefaultR-TeacherId}/courses/default': [],
  '/2/teachers/{TeacherStudentsLatestLoginEventsR-TeacherId}/students/latest-login-events': [],
  '/2/teachers/{TeacherStudentsPlacementSummariesR-TeacherId}/students/placement-summaries': [],
  '/2/teachers/invite': [],
  '/2/teachers/{TeacherClaimedFreeResourcesP-TeacherId}/claimed-free-resources': [],
  '/2/colleagues': [],
  '/2/school-admins': [],
  '/2/school-admins/{SchoolAdminImpressionsR-SchoolAdminId}/impression': [],
  '/2/school-admins/bulk-upload': [],
  '/2/school-admins/me': [],
  '/2/school-admins/me/teachers': [],
  '/2/school-admins/me/dashboard': [],
  '/2/school-admins/me/teachers-missing-emails': [],
  '/2/school-admins/sessions': [],
  '/2/school-admins/teacher-sessions': [],
  '/2/school-admins/{SchoolAdminP-SchoolAdminId}': [],
  '/2/school-admins/{SchoolAdminP-SchoolAdminId}/credentials': [],
  '/2/school-admins/{SchoolAdminP-SchoolAdminId}/rosters': [],
  '/2/school-admins/forgot-password': [],
  '/2/email-verifications': [],
  '/2/passwords': [],
  '/2/passwords/resets': [],
  '/2/connection-check': [],
  '/2/vocab-words/{ElaVocabWordsR-NonEmptyVocabWord}': [],
  '/2/sight-words/{ElaSightWordsR-ElaSightWordsLevel}': [],
  '/2/sight-words-level': [],
  '/2/sight-words-sessions': [],
  '/2/ela-assignments': [],
  '/2/ela-assignments/adaptive-skills/{ElaAdaptiveSkillsAssignmentSessionR-ElaAdaptiveSkillsPracticeAssignmentId}/session':
    [],
  '/2/ela-assignments/adaptive-skills/{ElaAdaptiveSkillsAnswersR-ElaAdaptiveSkillsPracticeAssignmentId}/answers':
    [],
  '/2/ela-assignments/adaptive-skills/{ElaAdaptiveSkillsPassagesR-ElaAdaptiveSkillsPracticeAssignmentId}/passages':
    [],
  '/2/ela-assignments/adaptive-skills/{ElaAdaptiveSkillsAssignmentExitR-NonEmptyElaAdaptiveSkillsPracticeAssignmentId}/exit':
    [],
  '/2/ela-assignments/targeted-skills/{ElaTargetedSkillsAssignmentP-ElaTargetedSkillsPracticeAssignmentId}/exit':
    [],
  '/2/ela-assignments/targeted-skills/{ElaTargetedSkillsAssignmentP-ElaTargetedSkillsPracticeAssignmentId}/answers':
    [],
  '/2/ela-assignments/targeted-skills/{ElaTargetedSkillsAssignmentP-ElaTargetedSkillsPracticeAssignmentId}/sessions':
    [],
  '/2/ela-assignments/targeted-skills/{ElaTargetedSkillsAssignmentP-ElaTargetedSkillsPracticeAssignmentId}/passages':
    [],
  '/2/ela-assignments/reading/{ElaArticleReadingAssignmentP-NonEmptyElaArticleReadingAssignmentId}/answers':
    [],
  '/2/ela-assignments/reading/{ElaArticleReadingAssignmentP-NonEmptyElaArticleReadingAssignmentId}/answers/retries':
    [],
  '/2/ela-assignments/reading/{ElaArticleReadingAssignmentP-NonEmptyElaArticleReadingAssignmentId}/sessions':
    [],
  '/2/ela-assignments/reading/{ElaArticleReadingAssignmentP-NonEmptyElaArticleReadingAssignmentId}/exit':
    [],
  '/2/ela-assignments/writing/{ElaArticleWritingAssignmentP-NonEmptyElaArticleWritingAssignmentId}/answers':
    [],
  '/2/ela-assignments/writing/{ElaArticleWritingAssignmentP-NonEmptyElaArticleWritingAssignmentId}/sessions':
    [],
  '/2/ela-assignments/assessments/{ElaAssessmentAssignmentP-NonEmptyElaAssessmentAssignmentId}/answers':
    [],
  '/2/ela-assignments/assessments/{ElaAssessmentAssignmentP-NonEmptyElaAssessmentAssignmentId}/ela-assessment-report':
    [],
  '/2/ela-assignments/assessments/{ElaAssessmentAssignmentP-NonEmptyElaAssessmentAssignmentId}/sessions':
    [],
  '/2/ela-assignments/decodables': [],
  '/2/ela-assignments/decodables/{ElaDecodablesAssignmentP-NonEmptyElaDecodablesAssignmentId}/answers':
    [],
  '/2/ela-assignments/decodables/{ElaDecodablesAssignmentP-NonEmptyElaDecodablesAssignmentId}/sessions':
    [],
  '/2/ela-assignments/word-study/sessions/progress': [],
  '/2/ela-assignments/word-study/{ElaWordStudyAssignmentR-WordStudyAssignmentId}': [],
  '/2/ela-assignments/word-study/{ElaWordStudyAssignmentP-NonEmptyWordStudyAssignmentId}/sessions':
    [],
  '/2/ela-assessment-session-report/{ElaAssessmentSessionReportR-ElaAssessmentAssignmentSessionId}':
    [],
  '/2/ela-assignment-answers/writing/{ElaArticleWritingAnswerR-ElaArticleWritingAnswerId}': [],
  '/2/ela-snapshots': [],
  '/2/ibl-assignments/{IblAssignmentsCompletionR-MathIblAssignmentId}/completion': [],
  '/2/assignment-sessions/adaptive/{MathAdaptiveAssignmentSessionAnswersR-MathAdaptiveAssignmentSessionId}/answers':
    [],
  '/2/math-assignments': [],
  '/2/math-assignments/assignment-sessions/is-any-session-complete': [],
  '/2/math-assignments/adaptive/{MathAdaptiveAssignmentP-NonEmptyMathAdaptiveAssignmentId}/assignment-sessions':
    [],
  '/2/math-assignments/assessment/{MathAssessmentAssignmentsP-NonEmptyMathAssessmentAssignmentId}/assignment-sessions':
    [],
  '/2/math-assignments/fact-practice/{MathFactPracticeAssignmentsP-NonEmptyMathFactPracticeAssignmentId}/assignment-sessions':
    [],
  '/2/math-assignments/ibl/{MathIblAssignmentsP-NonEmptyMathIblAssignmentId}/assignment-sessions':
    [],
  '/2/math-assignments/standard/{MathStandardAssignmentsP-NonEmptyMathStandardAssignmentId}/questions':
    [],
  '/2/math-assignments/standard/{MathStandardAssignmentsP-NonEmptyMathStandardAssignmentId}/assignment-sessions':
    [],
  '/2/math-targeted-assignments/{MathTargetedAssignmentsR-MathStandardAssignmentId}': [],
  '/2/math-adaptive-assignments/{MathAdaptiveAssignmentR-MathAdaptiveAssignmentId}': [],
  '/2/math-targeted-assignment-sessions/{MathTargetedAssignmentSessionsAnswersR-MathStandardAssignmentSessionId}/answers':
    [],
  '/2/referrals': [],
  '/2/math/number-facts/assignments/{NumberFactsAssignmentsP-NonEmptyNumberFactsAssignmentId}/sessions':
    [],
  '/2/math/number-facts/questions': [],
  '/2/math/number-facts/sessions': [],
  '/2/math/number-facts/levels': [],
  '/2/social-studies-assignments': [],
  '/2/social-studies-assignments/reading/{SocialStudiesReadingAssignmentR-SocialStudiesReadingAssignmentId}':
    [],
  '/2/social-studies-assignments/reading/{SocialStudiesReadingAssignmentP-NonEmptySocialStudiesReadingAssignmentId}/answers':
    [],
  '/2/social-studies-assignments/reading/{SocialStudiesReadingAssignmentP-NonEmptySocialStudiesReadingAssignmentId}/answers/retries':
    [],
  '/2/social-studies-assignments/reading/{SocialStudiesReadingAssignmentP-NonEmptySocialStudiesReadingAssignmentId}/sessions':
    [],
  '/2/social-studies-assignments/reading/{SocialStudiesReadingAssignmentP-NonEmptySocialStudiesReadingAssignmentId}/exit':
    [],
  '/2/social-studies-assignments/writing/{SocialStudiesWritingAssignmentR-SocialStudiesWritingAssignmentId}':
    [],
  '/2/social-studies-assignments/writing/{SocialStudiesWritingAssignmentP-NonEmptySocialStudiesWritingAssignmentId}/answers':
    [],
  '/2/social-studies-assignments/writing/{SocialStudiesWritingAssignmentP-NonEmptySocialStudiesWritingAssignmentId}/sessions':
    [],
  '/2/social-studies-writing-assignments-answers/{SocialStudiesWritingAssignmentAnswerR-SocialStudiesWritingAssignmentAnswerId}':
    [],
  '/2/science-assignments': [],
  '/2/science-assignments/reading/{ScienceReadingAssignmentR-ScienceReadingAssignmentId}': [],
  '/2/science-assignments/reading/{ScienceReadingAssignmentP-NonEmptyScienceReadingAssignmentId}/answers':
    [],
  '/2/science-assignments/reading/{ScienceReadingAssignmentP-NonEmptyScienceReadingAssignmentId}/answers/retries':
    [],
  '/2/science-assignments/reading/{ScienceReadingAssignmentP-NonEmptyScienceReadingAssignmentId}/sessions':
    [],
  '/2/science-assignments/reading/{ScienceReadingAssignmentP-NonEmptyScienceReadingAssignmentId}/exit':
    [],
  '/2/science-assignments/writing/{ScienceWritingAssignmentR-ScienceWritingAssignmentId}': [],
  '/2/science-assignments/writing/{ScienceWritingAssignmentP-NonEmptyScienceWritingAssignmentId}/answers':
    [],
  '/2/science-assignments/writing/{ScienceWritingAssignmentP-NonEmptyScienceWritingAssignmentId}/sessions':
    [],
  '/2/science-writing-assignments-answers/{ScienceWritingAssignmentAnswerR-ScienceWritingAssignmentAnswerId}':
    [],
  '/2/ela/article-search': [],
  '/2/ela/adaptive-skills/contents': [],
  '/2/ela/decodables/highest': [],
  '/2/periscope-embeds/school-usage': [],
  '/2/periscope-embeds/schools/{PeriscopeEmbedsSchoolGrowthR-SchoolId}/growth': [],
  '/2/periscope-embeds/districts/growth': [],
  '/2/periscope-embeds/teachers/{PeriscopeEmbedsTeacherGrowthR-TeacherId}/growth': [],
  '/2/periscope-embeds/students/{PeriscopeEmbedsStudentGrowthR-StudentId}/growth': [],
  '/2/periscope-embeds/skill-details/{PeriscopeEmbedsSkillDetailsR-UspId}': [],
  '/3/assignment-count': [],
  '/3/content-feedback': [],
  '/3/login-cards': [],
  '/3/subjects/{SubjectP-Subject}/academic-vocabulary': [],
  '/3/courses': [],
  '/3/courses/{V3CourseP-CourseId}/math-adaptive-domains-practiced': [],
  '/3/courses/{V3CourseP-CourseId}/math-assessments/growth-report': [],
  '/3/courses/{V3CourseP-CourseId}/math-assessments/standard-growth-report': [],
  '/3/courses/{V3CourseP-CourseId}/math-assessments/domain-growth-report': [],
  '/3/courses/{V3CourseP-CourseId}/math-assessments/student-growth-report': [],
  '/3/courses/{V3CourseP-CourseId}/ela-adaptive-skills/skill-groups-report': [],
  '/3/courses/{V3CourseP-CourseId}/ela-adaptive-skills/adaptive-progresses': [],
  '/3/schools/{V3SchoolP-SchoolId}/math/standard-set': [],
  '/3/students/presence-events': [],
  '/3/students/impressions': [],
  '/3/students/duplicates/merge': [],
  '/3/students/{V3StudentP-StudentId}/math/domain-statuses': [],
  '/3/students/{V3StudentP-StudentId}/math/suggested-domain': [],
  '/3/students/{V3StudentP-StudentId}/math/progress': [],
  '/3/students/{V3StudentP-StudentId}/math/report-card': [],
  '/3/students/{V3StudentP-StudentId}/math/adaptive/rewards': [],
  '/3/students/{V3StudentP-StudentId}/math/adaptive/rewards/{StudentMathAdaptiveRewardR-MathAdaptiveRewardId}':
    [],
  '/3/students/{V3StudentP-StudentId}/practice-summary': [],
  '/3/teachers': [],
  '/3/operations/{OperationP-OperationId}': [],
  '/3/webhooks': [],
  '/3/webhooks/recurly': [],
  '/3/purchases': [],
  '/3/teacher-practice-modes': []
}
